import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Users, Truck, Award, Shield, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `/api`;

const BecomePartnerPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "",
    contact_person: "",
    email: "",
    phone: "",
    business_type: "",
    monthly_requirement: "",
    fabric_interests: [],
    message: ""
  });

  const benefits = [
    {
      icon: Award,
      title: "Priority Access",
      description: "Get first access to new fabric arrivals and limited stock items"
    },
    {
      icon: Truck,
      title: "Stable Supply",
      description: "Guaranteed stock allocation even during high-demand periods"
    },
    {
      icon: Shield,
      title: "Competitive Pricing",
      description: "Special pricing and volume discounts for regular customers"
    },
    {
      icon: Users,
      title: "Dedicated Support",
      description: "Personal account manager for all your fabric needs"
    }
  ];

  const fabricOptions = [
    "50s SLUB Pique",
    "50s NR Pique",
    "60s NR HERRINGBONE",
    "BOSS DOBBY",
    "50s NR PLAIN DYED",
    "All Collections"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFabricInterestChange = (fabric) => {
    setFormData(prev => {
      const interests = prev.fabric_interests.includes(fabric)
        ? prev.fabric_interests.filter(f => f !== fabric)
        : [...prev.fabric_interests, fabric];
      return { ...prev, fabric_interests: interests };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.contact_person || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/partners`, formData);
      setSubmitted(true);
      toast.success("Thank you for your interest! We'll contact you soon.");
    } catch {
      // Submission error
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0]" data-testid="partner-success">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-[#4A5D4E] rounded-full">
            <CheckCircle size={40} className="text-white" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-normal text-[#1C1C1A] mb-4">
            Application Received!
          </h1>
          <p className="text-[#6B6A65] font-light mb-8">
            Thank you for your interest in becoming a regular customer. 
            Our team will review your application and contact you within 24-48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="btn-primary">
              Browse Collections
            </Link>
            <Link to="/" className="btn-secondary">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="become-partner-page">
      <Helmet>
        <title>Become a Regular Customer | DUGARS</title>
        <meta name="description" content="Partner with DUGARS for reliable, quality-assured fabric supply. Priority access, competitive pricing, and dedicated support for regular customers." />
        <link rel="canonical" href="https://thedugars.in/become-partner" />
        <meta property="og:title" content="Become a Regular Customer | DUGARS" />
        <meta property="og:description" content="Partner with DUGARS for reliable, quality-assured fabric supply." />
        <meta property="og:url" content="https://thedugars.in/become-partner" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#1C1C1A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="/api/images/partner_page.png"
            alt="Fabric warehouse"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">
            Partner With Us
          </span>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#F7F5F0] mb-6">
            Become a Regular Customer
          </h1>
          <p className="text-[#F7F5F0]/70 font-light max-w-2xl mx-auto">
            Join our network of trusted partners and enjoy priority access, 
            stable supply, and competitive pricing on all our premium fabrics.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-normal text-[#1C1C1A]">
              Benefits of Being a Regular Customer
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => (
              <div 
                key={benefit.title} 
                className="text-center p-6 border border-[#E6E4DD] hover:border-[#4A5D4E] transition-colors"
                data-testid={`benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-[#F7F5F0]">
                  <benefit.icon size={24} className="text-[#4A5D4E]" />
                </div>
                <h3 className="font-medium text-[#1C1C1A] mb-2">{benefit.title}</h3>
                <p className="text-sm text-[#6B6A65] font-light">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">
              Registration
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-normal text-[#1C1C1A]">
              Apply to Join Our Network
            </h2>
            <p className="text-[#6B6A65] font-light mt-4">
              Fill out the form below and our team will get in touch with you shortly.
            </p>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="bg-white p-8 md:p-12 border border-[#E6E4DD] space-y-6"
            data-testid="partner-form"
          >
            {/* Business Information */}
            <div>
              <h3 className="font-medium text-[#1C1C1A] mb-4 pb-2 border-b border-[#E6E4DD]">
                Business Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                    Business Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    className="custom-input w-full"
                    placeholder="Your company name"
                    required
                    data-testid="input-business-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                    Business Type
                  </label>
                  <select
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleChange}
                    className="custom-input w-full"
                    data-testid="select-business-type"
                  >
                    <option value="">Select type</option>
                    <option value="manufacturer">Garment Manufacturer</option>
                    <option value="wholesaler">Fabric Wholesaler</option>
                    <option value="retailer">Retailer</option>
                    <option value="designer">Fashion Designer</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="font-medium text-[#1C1C1A] mb-4 pb-2 border-b border-[#E6E4DD]">
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                    Contact Person <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleChange}
                    className="custom-input w-full"
                    placeholder="Your name"
                    required
                    data-testid="input-contact-person"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="custom-input w-full"
                    placeholder="+91 XXXXXXXXXX"
                    required
                    data-testid="input-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="custom-input w-full"
                    placeholder="your@email.com"
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div>
              <h3 className="font-medium text-[#1C1C1A] mb-4 pb-2 border-b border-[#E6E4DD]">
                Your Requirements
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1C1C1A] mb-3">
                  Fabric Interests
                </label>
                <div className="flex flex-wrap gap-3">
                  {fabricOptions.map((fabric) => (
                    <label 
                      key={fabric}
                      className={`flex items-center gap-2 px-4 py-2 border cursor-pointer transition-colors ${
                        formData.fabric_interests.includes(fabric)
                          ? 'border-[#4A5D4E] bg-[#4A5D4E]/5'
                          : 'border-[#E6E4DD] hover:border-[#4A5D4E]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.fabric_interests.includes(fabric)}
                        onChange={() => handleFabricInterestChange(fabric)}
                        className="sr-only"
                      />
                      <span className={`w-4 h-4 border flex items-center justify-center ${
                        formData.fabric_interests.includes(fabric)
                          ? 'border-[#4A5D4E] bg-[#4A5D4E]'
                          : 'border-[#E6E4DD]'
                      }`}>
                        {formData.fabric_interests.includes(fabric) && (
                          <CheckCircle size={12} className="text-white" />
                        )}
                      </span>
                      <span className="text-sm">{fabric}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                  Estimated Monthly Requirement
                </label>
                <select
                  name="monthly_requirement"
                  value={formData.monthly_requirement}
                  onChange={handleChange}
                  className="custom-input w-full"
                  data-testid="select-monthly-requirement"
                >
                  <option value="">Select range</option>
                  <option value="under-500">Under 500 meters</option>
                  <option value="500-1000">500 - 1,000 meters</option>
                  <option value="1000-5000">1,000 - 5,000 meters</option>
                  <option value="5000-10000">5,000 - 10,000 meters</option>
                  <option value="over-10000">Over 10,000 meters</option>
                </select>
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                Additional Information
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="custom-input w-full resize-none"
                placeholder="Tell us more about your business and specific requirements..."
                data-testid="input-message"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="partner-submit-btn"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <Send size={18} />
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BecomePartnerPage;
